import type { ExportReportRow } from './exportTypes';

export type ExportColumnId =
  | 'date'
  | 'status'
  | 'reportType'
  | 'hospital'
  | 'employee'
  | 'employmentRole'
  | 'quantity'
  | 'ratePerUnit'
  | 'hospitalBonus'
  | 'amount';

export type ExportGroupBy = 'hospital' | 'employee' | 'reportType' | 'flat';

export type ExportTemplate = 'detailed' | 'summary' | 'payroll' | 'status';

export interface ExportColumnDef {
  id: ExportColumnId;
  labelKey: string;
  header: string;
  csvWidth: number;
  pdfWidth: string;
  getValue: (r: ExportReportRow) => string | number;
}

export const ALL_EXPORT_COLUMNS: ExportColumnDef[] = [
  {
    id: 'date',
    labelKey: 'export.wizard.colDate',
    header: 'Date',
    csvWidth: 14,
    pdfWidth: '10%',
    getValue: (r) => r.date,
  },
  {
    id: 'status',
    labelKey: 'export.wizard.colStatus',
    header: 'Status',
    csvWidth: 12,
    pdfWidth: '9%',
    getValue: (r) => r.status,
  },
  {
    id: 'reportType',
    labelKey: 'export.wizard.colReportType',
    header: 'Type',
    csvWidth: 18,
    pdfWidth: '11%',
    getValue: (r) => r.reportType,
  },
  {
    id: 'hospital',
    labelKey: 'export.wizard.colHospital',
    header: 'Hospital',
    csvWidth: 22,
    pdfWidth: '22%',
    getValue: (r) => r.hospital,
  },
  {
    id: 'employee',
    labelKey: 'export.wizard.colEmployee',
    header: 'Employee',
    csvWidth: 20,
    pdfWidth: '14%',
    getValue: (r) => r.employee ?? '',
  },
  {
    id: 'employmentRole',
    labelKey: 'export.wizard.colRole',
    header: 'Role',
    csvWidth: 16,
    pdfWidth: '10%',
    getValue: (r) => r.employmentRole ?? '',
  },
  {
    id: 'quantity',
    labelKey: 'export.wizard.colQty',
    header: 'Qty',
    csvWidth: 8,
    pdfWidth: '5%',
    getValue: (r) => r.quantity,
  },
  {
    id: 'ratePerUnit',
    labelKey: 'export.wizard.colRate',
    header: 'Rate',
    csvWidth: 10,
    pdfWidth: '6%',
    getValue: (r) => (r.ratePerUnit > 0 ? r.ratePerUnit : '-'),
  },
  {
    id: 'hospitalBonus',
    labelKey: 'export.wizard.colBonus',
    header: 'Bonus',
    csvWidth: 10,
    pdfWidth: '6%',
    getValue: (r) => (r.hospitalBonus > 0 ? r.hospitalBonus : '-'),
  },
  {
    id: 'amount',
    labelKey: 'export.wizard.colAmount',
    header: 'Amount',
    csvWidth: 14,
    pdfWidth: '10%',
    getValue: (r) => r.amount,
  },
];

export const ALL_COLUMN_IDS: ExportColumnId[] = ALL_EXPORT_COLUMNS.map((c) => c.id);

export const EXPORT_TEMPLATES: Record<ExportTemplate, { labelKey: string; columns: ExportColumnId[] }> = {
  detailed: {
    labelKey: 'export.wizard.templateDetailed',
    columns: [...ALL_COLUMN_IDS],
  },
  summary: {
    labelKey: 'export.wizard.templateSummary',
    columns: ['date', 'status', 'reportType', 'hospital', 'employee', 'quantity', 'amount'],
  },
  payroll: {
    labelKey: 'export.wizard.templatePayroll',
    columns: ['employee', 'reportType', 'quantity', 'ratePerUnit', 'hospitalBonus', 'amount'],
  },
  status: {
    labelKey: 'export.wizard.templateStatus',
    columns: ['date', 'status', 'reportType', 'hospital', 'employee', 'amount'],
  },
};

export const GROUP_BY_OPTIONS: { id: ExportGroupBy; labelKey: string; icon: string }[] = [
  { id: 'hospital', labelKey: 'export.wizard.groupByHospital', icon: 'Building2' },
  { id: 'employee', labelKey: 'export.wizard.groupByEmployee', icon: 'User' },
  { id: 'reportType', labelKey: 'export.wizard.groupByReportType', icon: 'FileText' },
  { id: 'flat', labelKey: 'export.wizard.groupByFlat', icon: 'List' },
];

const columnMap = new Map(ALL_EXPORT_COLUMNS.map((c) => [c.id, c]));

export function getColumnDefs(ids: ExportColumnId[]): ExportColumnDef[] {
  return ids.map((id) => columnMap.get(id)).filter(Boolean) as ExportColumnDef[];
}
